import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { CommonRootState } from '../combineReducers';
import { IServerTokens, traceHeaderName } from '../interfaces/serverTokens';
import {
  registerAcceptLanguageInterceptor,
  registerBearerTokenInterceptor,
  registerTraceHeaderInterceptor
} from '../helpers/interceptors';
import { ThunkAction } from 'redux-thunk';

const fetchServerTokensRequest = () =>
  ({
    type: 'FETCH_SERVER_TOKENS_REQUEST'
  } as const);

const fetchServerTokensSuccess = (payload: IServerTokens) =>
  ({
    type: 'FETCH_SERVER_TOKENS_SUCCESS',
    payload
  } as const);

const fetchServerTokensFailure = (error: AxiosError) => {
  console.error(error);
  return {
    type: 'FETCH_SERVER_TOKENS_FAILURE',
    error
  } as const;
};

const logOut = () =>
  ({
    type: 'LOGOUT'
  } as const);

const gmapsLoaded = () =>
  ({
    type: 'GMAPS_LOADED'
  } as const);

const pendoLoaded = () =>
  ({
    type: 'PENDO_LOADED'
  } as const);

export const handleLogOut = (
  logoutUrl = '/spapi/auth/logout'
): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  dispatch(logOut());
  Cookies.remove('AionCurrentOrganizationId');
  window.location.assign(logoutUrl);
};

declare global {
  interface Window {
    _AION_gmapsLoaded: () => void;
  }
}

export const registerServerTokens = (
  serverTokens: IServerTokens
): ThunkAction<void, CommonRootState, null, Actions> => dispatch => {
  window._AION_gmapsLoaded = () => {
    dispatch(gmapsLoaded());
  };
  appendGoogleMapsScript(serverTokens.googleMapsApiKey);
  dispatch(appendPendoScript(serverTokens.pendoApiKey));
  registerBearerTokenInterceptor(serverTokens.accessToken, error => dispatch(fetchServerTokensFailure(error)));
  registerTraceHeaderInterceptor(serverTokens[traceHeaderName]);
  registerAcceptLanguageInterceptor(); // TODO: Discuss whether this should be moved to RegisterInterceptors.tsx (aion-ui-components)
  dispatch(fetchServerTokensSuccess(serverTokens));
};

const appendGoogleMapsScript = (googleMapsApiKey: string) => {
  const gmaps = document.createElement('script');
  gmaps.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&callback=_AION_gmapsLoaded&libraries=places`;
  gmaps.defer = true;
  document.head.appendChild(gmaps);
};

const appendPendoScript = (
  pendoApiKey: string
): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  const pendo = document.createElement('script');
  pendo.src = `https://cdn.pendo.io/agent/static/${pendoApiKey}/pendo.js`;
  pendo.defer = true;
  document.head.appendChild(pendo);

  pendo.addEventListener(
    'load',
    () => {
      dispatch(pendoLoaded());
    },
    { once: true }
  );
};

const fetchServerTokens = (): ThunkAction<void, CommonRootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchServerTokensRequest());

    const {
      data: { serverTokens, redirectUri }
    } = await axios.get<{ serverTokens?: IServerTokens; redirectUri?: string }>('/spapi/serverTokens');

    if (serverTokens) {
      dispatch(registerServerTokens(serverTokens));
    } else if (redirectUri) {
      window.location.assign(redirectUri);
    }
  } catch (error) {
    dispatch(fetchServerTokensFailure(error));
  }
};

const shouldFetchServerTokens = ({ serverTokens: { isFetching, serverTokens } }: CommonRootState) =>
  !isFetching && !serverTokens;

export const fetchServerTokensIfNeeded = (): ThunkAction<void, CommonRootState, null, Actions> => (
  dispatch,
  getState
) => {
  if (shouldFetchServerTokens(getState())) {
    dispatch(fetchServerTokens());
  }
};

export type Actions =
  | ReturnType<typeof fetchServerTokensRequest>
  | ReturnType<typeof fetchServerTokensSuccess>
  | ReturnType<typeof fetchServerTokensFailure>
  | ReturnType<typeof gmapsLoaded>
  | ReturnType<typeof logOut>
  | ReturnType<typeof pendoLoaded>;
